'use strict';

import { logInfo, logWarn, logError } from './liveShoppingBambuser/core/bambuserLogger';
import { getCartIntegrationConfig } from './liveShoppingBambuser/core/bambuserCartConfig';
import { configurePlayerCartIntegration } from './liveShoppingBambuser/cart/bambuserPlayerEvents';

// ---------------------------------------------------------------------------
// Bambuser SDK ready handler
//
// The SDK snapshots window.onBambuserLiveShoppingReady at load time and may
// overwrite or clear it afterwards.  Object.defineProperty makes the getter
// always return our handler, so the SDK can never drop it — every new player
// gets cart-integration events regardless of SDK lifecycle quirks.
// ---------------------------------------------------------------------------

var _previousReadyHandler = null;
var _playerCreated = false;

function getSdkWindow () {
    var sdkWindow = window;
    try {
        if (window.top && window.top.document) {
            sdkWindow = window.top;
        }
    } catch (e) {
        sdkWindow = window;
    }

    return sdkWindow;
}

function getSdkDocument (sdkWindow) {
    try {
        if (sdkWindow && sdkWindow.document) {
            return sdkWindow.document;
        }
    } catch (e) {
        // Fall through to current document
    }

    return document;
}

function bambuserReadyHandler (player) {
    _playerCreated = true;
    logInfo('onBambuserLiveShoppingReady fired');
    var config = getCartIntegrationConfig();
    configurePlayerCartIntegration(player, config);

    if (typeof _previousReadyHandler === 'function') {
        _previousReadyHandler(player);
    }
}

(function installReadyHandler () {
    var sdkWindow = getSdkWindow();
    var existing = sdkWindow.onBambuserLiveShoppingReady;
    if (typeof existing === 'function' && existing !== bambuserReadyHandler) {
        _previousReadyHandler = existing;
    }

    try {
        Object.defineProperty(sdkWindow, 'onBambuserLiveShoppingReady', {
            configurable: true,
            enumerable: true,
            get: function () {
                return bambuserReadyHandler;
            },
            set: function (fn) {
                // Capture any handler the SDK or other code tries to set;
                // the getter still returns our wrapper so we are never lost.
                if (typeof fn === 'function' && fn !== bambuserReadyHandler) {
                    _previousReadyHandler = fn;
                }
            },
        });
    } catch (e) {
        // Fallback for environments where defineProperty on window fails
        sdkWindow.onBambuserLiveShoppingReady = bambuserReadyHandler;
    }
})();

// ---------------------------------------------------------------------------
// SDK script injection
// ---------------------------------------------------------------------------

function ensureBambuserScript (sdkWindow, sdkDoc) {
    if (sdkWindow.initBambuserLiveShopping) {
        return;
    }

    sdkWindow.initBambuserLiveShopping = function (item) {
        sdkWindow.initBambuserLiveShopping.queue.push(item);
    };
    sdkWindow.initBambuserLiveShopping.queue = [];

    var config = getCartIntegrationConfig();

    // CoreMedia fragments may already have emitted a server-side <script src="…embed.js">.
    // In that case just install the queue stub above and let the existing tag load the SDK.
    if (sdkDoc.querySelector('script[src="' + config.sdkUrl + '"]')) {
        return;
    }

    var script = sdkDoc.createElement('script');
    script.src = config.sdkUrl;
    script.onload = function () {
        logInfo('Bambuser SDK loaded');
    };
    script.onerror = function () {
        logError('Bambuser SDK failed to load');
    };

    if (sdkDoc.body) {
        sdkDoc.body.appendChild(script);
    } else {
        sdkDoc.documentElement.appendChild(script);
    }
}

function isStaleBambuserContainer (node, sdkDoc) {
    if (!node) {
        return false;
    }

    if (node.dataset && node.dataset.bambuserCheckoutClosing === '1') {
        return true;
    }

    var inlineDisplay = node.style && node.style.display;
    var inlineOpacity = node.style && node.style.opacity;
    if (inlineDisplay === 'none' || inlineOpacity === '0') {
        return true;
    }

    try {
        var view = sdkDoc.defaultView || window;
        if (view && typeof view.getComputedStyle === 'function') {
            var computed = view.getComputedStyle(node);
            if (computed) {
                return computed.display === 'none' ||
                    computed.visibility === 'hidden' ||
                    computed.opacity === '0';
            }
        }
    } catch (e) {
        // Ignore style read failures and treat as non-stale.
    }

    return false;
}

function getDetachedBin (sdkWindow, sdkDoc) {
    if (!sdkWindow.__bambuserDetachedContainerBin) {
        sdkWindow.__bambuserDetachedContainerBin = sdkDoc.createElement('div');
    }

    return sdkWindow.__bambuserDetachedContainerBin;
}

function detachStaleBambuserContainers (sdkWindow, sdkDoc) {
    var containers = sdkDoc.querySelectorAll('[data-bambuser-liveshopping-player-id]');
    if (!containers.length) {
        return;
    }

    var bin = getDetachedBin(sdkWindow, sdkDoc);
    for (var i = 0; i < containers.length; i++) {
        var container = containers[i];
        if (!container || !container.parentNode) {
            continue;
        }

        if (!isStaleBambuserContainer(container, sdkDoc)) {
            continue;
        }

        if (container.parentNode !== bin) {
            bin.appendChild(container);
        }
    }
}

// ---------------------------------------------------------------------------
// Show trigger
// ---------------------------------------------------------------------------

function openShow (showId) {
    var trimmedId = showId.trim();
    var sdkWindow = getSdkWindow();
    var sdkDoc = getSdkDocument(sdkWindow);

    detachStaleBambuserContainers(sdkWindow, sdkDoc);

    ensureBambuserScript(sdkWindow, sdkDoc);
    sdkWindow.initBambuserLiveShopping({
        showId: trimmedId,
        type: 'overlay',
    });
}

function handleClick (event) {
    var showId = this.getAttribute('data-show-id');
    if (!showId) {
        logWarn('trigger click ignored: missing data-show-id');
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    openShow(showId);
}

window.addEventListener('pageshow', function (event) {
    if (event.persisted && _playerCreated) {
        window.location.reload();
    }
});

// ---------------------------------------------------------------------------
// Bind trigger buttons
// ---------------------------------------------------------------------------

function bindAll (root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('.js-liveShoppingBambuserTrigger');
    var hasPlaylist = scope.querySelector('bam-playlist') !== null;

    if (nodes.length > 0 || hasPlaylist) {
        var sdkWindow = getSdkWindow();
        var sdkDoc = getSdkDocument(sdkWindow);
        ensureBambuserScript(sdkWindow, sdkDoc);
    }

    for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].getAttribute('data-bambuser-bound') !== 'true') {
            nodes[i].addEventListener('click', handleClick);
            nodes[i].setAttribute('data-bambuser-bound', 'true');
        }
    }
}

window.liveShoppingBambuser = { bindAll: bindAll };

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
        bindAll(document);
    });
} else {
    bindAll(document);
}
